import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import type { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { getStandardDrawerFloatingSx, useStandardDrawerLayerContainer } from 'components/Drawers/StandardDrawer';

import type { QMenuSubProps } from '../types';
import { QMenuCloseProvider, useQMenuClose } from './QMenuCloseContext';
import { Q_MENU_END_ICON_SX, Q_MENU_ICON_SLOT_SIZE, Q_MENU_ICON_SLOT_SX } from './iconSlot';
import { QMenuSubLevelProvider, useQMenuSubLevel } from './QMenuSubLevelContext';

/** Delay before hover opens the sub (avoids flicker while crossing siblings). */
const HOVER_OPEN_MS = 100;
/** Delay before hover closes — enough to move pointer onto the nested panel. */
const HOVER_CLOSE_MS = 200;
/** Gap between parent trigger and nested paper (NestedQMenu ~5px habit). */
const FLYOUT_GAP_PX = 4;
/** Match MUI Popover default marginThreshold so we flip before clamp slides over the parent. */
const VIEWPORT_MARGIN_PX = 16;

type FlyoutPlacement = 'right' | 'left';

/** Pre-layout estimate for numeric / px widths only — not rem, %, etc. */
function resolveFlyoutWidthEstimate(width: number | string): number {
  if (typeof width === 'number' && Number.isFinite(width)) {
    return width;
  }
  if (typeof width === 'string') {
    const trimmed = width.trim();
    const pxMatch = /^(\d+(?:\.\d+)?)px$/.exec(trimmed);
    if (pxMatch) {
      return Number.parseFloat(pxMatch[1]);
    }
    if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
      return Number.parseFloat(trimmed);
    }
  }
  // rem / % / calc — wait for rendered paper; minWidth until then.
  return 180;
}

function measureFlyoutWidthPx(paper: HTMLElement | null, width: number | string): number {
  const renderedWidth = paper?.getBoundingClientRect().width;
  if (renderedWidth !== undefined && renderedWidth > 0) {
    return renderedWidth;
  }
  return resolveFlyoutWidthEstimate(width);
}

function chooseFlyoutPlacement(trigger: DOMRect, flyoutWidthPx: number): FlyoutPlacement {
  const fitsRight = trigger.right + FLYOUT_GAP_PX + flyoutWidthPx <= window.innerWidth - VIEWPORT_MARGIN_PX;
  return fitsRight ? 'right' : 'left';
}

/**
 * Parent row that opens a nested menu panel (Radix-style Sub, single-component v1).
 * Supported depth: 2 levels of QMenuSub (root → sub → sub-sub). Open via hover, click, or ArrowRight.
 */
export function QMenuSub(props: QMenuSubProps): React.ReactElement {
  const {
    label,
    subtext,
    startIcon,
    emptyStartIcon = false,
    disabled = false,
    dense,
    id: idProp,
    autoFocus,
    tabIndex,
    children,
    width = 180,
    selected = false,
    showCheckmark = false,
  } = props;

  const reactId = useId();
  const subId = idProp ?? `q-menu-sub${reactId}`;
  const menuId = `${subId}-menu`;

  const level = useQMenuSubLevel();
  const parentMenuClose = useQMenuClose();
  const [localOpen, setLocalOpen] = useState(false);
  const open = level ? level.activeId === subId : localOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (level) {
        if (next) {
          level.setActiveId(subId);
        } else {
          // Sibling hover-close timers are per-instance; only clear if we still own the slot.
          level.clearActiveIdIf(subId);
        }
        return;
      }
      setLocalOpen(next);
    },
    [level, subId],
  );

  const triggerRef = useRef<HTMLLIElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedByKeyboardRef = useRef(false);
  const [placement, setPlacement] = useState<FlyoutPlacement>('right');

  const updatePlacement = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const flyoutWidthPx = measureFlyoutWidthPx(paperRef.current, width);
    setPlacement(chooseFlyoutPlacement(trigger.getBoundingClientRect(), flyoutWidthPx));
  }, [width]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    return () => window.removeEventListener('resize', updatePlacement);
  }, [open, updatePlacement]);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current !== null) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const scheduleOpen = useCallback(() => {
    if (disabled) return;
    clearTimers();
    // Switching between open siblings should feel instant; intent delay is for cold open only.
    const siblingAlreadyOpen = Boolean(level?.activeId && level.activeId !== subId);
    const delayMs = siblingAlreadyOpen ? 0 : HOVER_OPEN_MS;
    openTimerRef.current = setTimeout(() => {
      openedByKeyboardRef.current = false;
      setOpen(true);
    }, delayMs);
  }, [clearTimers, disabled, level?.activeId, setOpen, subId]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, HOVER_CLOSE_MS);
  }, [clearTimers, setOpen]);

  const openNow = useCallback(
    (fromKeyboard = false) => {
      if (disabled) return;
      clearTimers();
      openedByKeyboardRef.current = fromKeyboard;
      setOpen(true);
    },
    [clearTimers, disabled, setOpen],
  );

  const closeNow = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers, setOpen]);

  // If `disabled` flips true while this flyout is open (e.g. a permissions change), force it closed.
  useEffect(() => {
    if (disabled && open) {
      closeNow();
    }
  }, [disabled, open, closeNow]);

  /** Leaf auto-close: dismiss this flyout and the root (or parent sub) menu. */
  const closeFlyoutAndAncestors = useCallback(() => {
    closeNow();
    parentMenuClose?.close();
  }, [closeNow, parentMenuClose]);

  const handleTriggerClick = (event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;
    event.stopPropagation();
    event.preventDefault();
    if (open) {
      closeNow();
    } else {
      openNow(false);
    }
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (disabled) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      openNow(true);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (open) {
        closeNow();
      } else {
        openNow(true);
      }
    } else if (event.key === 'ArrowLeft' && open) {
      event.preventDefault();
      event.stopPropagation();
      closeNow();
    }
  };

  const handleNestedClose: NonNullable<MenuProps['onClose']> = (event, reason) => {
    if (reason === 'backdropClick') {
      // Nested menus share the parent overlay; ignore backdrop so the root stays open.
      return;
    }
    if (reason === 'escapeKeyDown') {
      if (event && typeof event === 'object' && 'stopPropagation' in event) {
        (event as { stopPropagation: () => void }).stopPropagation();
      }
      closeNow();
      triggerRef.current?.focus();
      return;
    }
    closeNow();
  };

  const handleNestedKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      closeNow();
      triggerRef.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Keep arrow navigation scoped to the nested MenuList. Without stopping propagation,
      // parent-level menu handlers can also process the key and effectively skip items.
      event.stopPropagation();
    }
  };

  const drawerLayerContainer = useStandardDrawerLayerContainer();
  const drawerFloatingSx = getStandardDrawerFloatingSx();

  const hasSubtext = Boolean(subtext);
  const selectedColor = selected ? 'primary.main' : undefined;
  // Match QMenuItem option: highlight background only when selected without a leading checkmark.
  const showSelectedHighlight = selected && !showCheckmark;

  const openToRight = placement === 'right';
  const paperSx: SxProps<Theme> = (theme) => ({
    width,
    minWidth: 180,
    maxWidth: '100%',
    maxHeight: 400,
    borderRadius: 'xl',
    border: 'none',
    py: theme.spacing(0.5),
    pointerEvents: 'auto',
    // Small gap so parent + flyout shadows do not fuse; mirrored when flipped left.
    ...(openToRight ? { ml: `${FLYOUT_GAP_PX}px` } : { mr: `${FLYOUT_GAP_PX}px` }),
  });

  const iconTopAlignSx = hasSubtext
    ? {
        alignSelf: 'flex-start' as const,
        mt: 0.25,
      }
    : undefined;

  const leadingNodes: ReactNode[] = [];
  if (showCheckmark) {
    leadingNodes.push(
      <ListItemIcon key='checkmark' sx={{ ...Q_MENU_ICON_SLOT_SX, ...iconTopAlignSx }}>
        <CheckRoundedIcon
          sx={{
            color: selected ? 'primary.main' : 'transparent',
            opacity: selected ? 1 : 0,
          }}
        />
      </ListItemIcon>,
    );
  }
  if (startIcon) {
    leadingNodes.push(
      <ListItemIcon key='start' sx={{ ...Q_MENU_ICON_SLOT_SX, color: selectedColor ?? 'inherit', ...iconTopAlignSx }}>
        {startIcon}
      </ListItemIcon>,
    );
  } else if (emptyStartIcon && !showCheckmark) {
    // Reserve the toggle/check column when this sub sits beside leading-affordance rows.
    leadingNodes.push(<ListItemIcon key='start' sx={{ ...Q_MENU_ICON_SLOT_SX, ...iconTopAlignSx }} />);
  }

  return (
    <>
      <MenuItem
        ref={triggerRef}
        id={subId}
        dense={dense}
        disabled={disabled}
        disableRipple
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        selected={showSelectedHighlight}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        aria-haspopup='menu'
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? menuId : undefined}
        sx={{
          borderRadius: 'lg',
          px: (theme) => theme.spacing(1),
          py: (theme) => theme.spacing(dense ? 0.5 : 1),
          alignItems: hasSubtext ? 'flex-start' : 'center',
          color: selectedColor,
          whiteSpace: 'normal',
          '& .MuiListItemIcon-root': {
            minWidth: Q_MENU_ICON_SLOT_SIZE,
            width: Q_MENU_ICON_SLOT_SIZE,
            justifyContent: 'center',
            color: selectedColor ?? 'inherit',
            ...iconTopAlignSx,
          },
        }}
      >
        {leadingNodes}
        <Stack sx={{ flex: 1, minWidth: 0, py: hasSubtext ? 0 : undefined }}>
          <Typography
            variant='paragraphRegular'
            component='span'
            sx={{ fontWeight: 400, color: selectedColor ?? 'text.primary' }}
          >
            {label}
          </Typography>
          {subtext ? (
            <Typography
              variant='captionRegular'
              color='text.secondary'
              component='span'
              sx={{ display: 'block', mt: 0.25 }}
            >
              {subtext}
            </Typography>
          ) : null}
        </Stack>
        <Stack
          component='span'
          sx={{
            ...Q_MENU_END_ICON_SX,
            color: selectedColor ?? 'text.secondary',
            ...iconTopAlignSx,
          }}
        >
          <ChevronRightRoundedIcon />
        </Stack>
      </MenuItem>
      {/* Providers wrap *outside* Menu — not as MenuList children. MUI MenuList clones
          its direct children for autoFocusItem / tabIndex; a Context.Provider as the sole
          child swallows that and Space/Enter/ArrowRight open the flyout without focusing. */}
      <QMenuCloseProvider onClose={closeFlyoutAndAncestors}>
        <QMenuSubLevelProvider>
          <Menu
            id={menuId}
            anchorEl={triggerRef.current}
            open={open}
            onClose={handleNestedClose}
            container={drawerLayerContainer ?? undefined}
            // Nested panel must not steal pointer events from the parent menu surface while
            // the pointer travels between trigger and paper (NestedMenu / MUI nested pattern).
            sx={{ ...drawerFloatingSx, pointerEvents: 'none' }}
            variant='menu'
            autoFocus={openedByKeyboardRef.current}
            disableAutoFocusItem={!openedByKeyboardRef.current}
            // Prefer opening to the side that fits; avoids Popover left-shift overlapping the parent.
            anchorOrigin={{ vertical: 'top', horizontal: openToRight ? 'right' : 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: openToRight ? 'left' : 'right' }}
            slotProps={{
              root: {
                // Avoid a second modal backdrop that would dismiss the parent menu.
                slotProps: {
                  backdrop: {
                    sx: { backgroundColor: 'transparent' },
                    invisible: true,
                  },
                },
              },
              paper: {
                ref: paperRef,
                sharedcomponentid: 'QMenu',
                sx: paperSx,
                elevation: 24,
                // Test / debug hook for placement side (PaperProps typing omits data-* attrs).
                ...({ 'data-placement': placement } as { 'data-placement': FlyoutPlacement }),
                onMouseEnter: () => {
                  clearTimers();
                },
                onMouseLeave: scheduleClose,
              },
              list: {
                dense,
                autoFocusItem: openedByKeyboardRef.current,
                onKeyDown: handleNestedKeyDown,
                'aria-labelledby': subId,
                sx: (theme) => ({
                  py: theme.spacing(0.5),
                }),
              },
            }}
          >
            {children}
          </Menu>
        </QMenuSubLevelProvider>
      </QMenuCloseProvider>
    </>
  );
}
